import { BasicColumnTypes } from '@opengov/ogql-types';
import { RelativeFilterOptions, isRelativeFilter } from '../accountSummaryRelativeFilters';
import { RelativeAsOfDateOptions } from '../relativeAsOfDateOptions';
import { getRelativeDateRange } from './relativeDateCalculation';
import { todayInTimezone } from './todayInTimezone';

/** @deprecated Use RelativeAsOfDateOptions from relativeAsOfDateOptions instead */
export const RelativeAsOfOptions = RelativeAsOfDateOptions;
export type RelativeAsOfOption = (typeof RelativeAsOfDateOptions)[keyof typeof RelativeAsOfDateOptions];

const TODAY_OPTIONS: ReadonlySet<string> = new Set([
  RelativeAsOfDateOptions.Today,
  RelativeAsOfDateOptions.CurrentMonth,
  RelativeAsOfDateOptions.CurrentPeriod,
  RelativeAsOfDateOptions.CurrentFiscalYear
]);

/**
 * asOf tokens that resolve to the last day of a relative range, mapped to that range.
 * A Map (not a plain object) so that a fixed-date asOf which happens to collide with an
 * Object.prototype key — "constructor", "toString" — misses here and falls through to the
 * passthrough return, rather than reaching lastDayOf and throwing.
 */
const LAST_DAY_OPTIONS: ReadonlyMap<string, string> = new Map([
  [RelativeAsOfDateOptions.PriorMonth, RelativeFilterOptions.PriorMonth],
  // PriorPeriod = prior calendar month, same resolution as PriorMonth.
  [RelativeAsOfDateOptions.PriorPeriod, RelativeFilterOptions.PriorMonth],
  [RelativeAsOfDateOptions.PriorFiscalYear, RelativeFilterOptions.PriorFiscalYear]
]);

function lastDayOf(relativeFilter: string, fiscalYearStartMonth: number, entityTimezone?: string | null): string {
  if (!isRelativeFilter(relativeFilter)) {
    throw new Error(`Invalid relative filter: ${relativeFilter}`);
  }
  const [, endDate] = getRelativeDateRange(
    relativeFilter,
    1,
    '',
    fiscalYearStartMonth,
    BasicColumnTypes.Date,
    entityTimezone
  );
  return endDate ?? '';
}

/**
 * Resolves a relative asOf string to a concrete date string, or passes through
 * fixed date strings unchanged. Returns '' when asOfDate is absent, preserving
 * the existing downstream behavior of treating empty as today.
 *
 * When entityTimezone is provided, "today" / current-* tokens resolve to that IANA
 * zone's calendar date; otherwise they fall back to UTC (legacy behavior).
 */
export function computeAsOfDate(
  asOfDate: string | undefined,
  fiscalYearStartMonth: number,
  entityTimezone?: string | null
): string {
  if (!asOfDate) return '';
  if (TODAY_OPTIONS.has(asOfDate)) return todayInTimezone(entityTimezone);

  const lastDayFilter = LAST_DAY_OPTIONS.get(asOfDate);
  if (lastDayFilter) {
    return lastDayOf(lastDayFilter, fiscalYearStartMonth, entityTimezone);
  }

  return asOfDate;
}
